/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Button, Typography, Box, Tabs, Tab } from '@mui/material';
import ZampSettings from './zamp_settings';
import DateRangePicker from './historical_sync';
import Logger from './Logs';
import DisconnectPage from "./disconnect_page";
import { navLogoUrl } from "./brandAssets";
import Integration from "./Integration";
import ConnectPage from "./ConnectPage";
import ErrorPage from "./ErrorPage";
import SyncQuotes from "./sync_quotes";
import SyncCreditNotes from "./sync_creditnotes";
import PositionedMenu from "./components/PositionedMenu";
import CompanyDropdown from "./components/CompanyDropdown";
import TaxCodeMappingUtility from "./taxcode_mapping";
import CustomerEntityMappingUtility from "./customerentity_mapping";


function App() {
    const location = useLocation();
    const hideTabsOnPaths = ['/disconnect', '/launch', '/error'];
    const shouldHideTabs = hideTabsOnPaths.some(path => location.pathname.startsWith(path));

    return (
        <>
            <NavBar />
            <Box mt={8}>
                {!shouldHideTabs && <TabsSection />}
                <Routes>
                    <Route path="/settings/:tenantId?" element={<ZampSettings />} />
                    <Route path="/services/:tenantId?" element={<DateRangePicker />} />
                    <Route path="/logs/:tenantId?" element={<Logger />} />
                    <Route path="/" element={<Navigate to={`/launch`} replace />} />
                    <Route path="/integration/:tenantId?" element={<Integration />} />
                    <Route path="/disconnect" element={<DisconnectPage />} />
                    <Route path="/launch" element={<ConnectPage />} />
                    <Route path="/error" element={<ErrorPage />} />
                    <Route path="/quotes/:tenantId?" element={<SyncQuotes />} />
                    <Route path="/creditnotes/:tenantId?" element={<SyncCreditNotes />} />
                    <Route path="/taxcodemapping/:tenantId?" element={<TaxCodeMappingUtility />} />
                    <Route path="/customerentitymapping/:tenantId?" element={<CustomerEntityMappingUtility />} />
                </Routes>
            </Box>
        </>
    );
}

function NavBar() {
    const [tenantId, setTenantId] = useState(sessionStorage.getItem("tenantId"));

    useEffect(() => {
        const handleTenantChange = () => setTenantId(sessionStorage.getItem("tenantId"));
        window.addEventListener("TenantIdChange", handleTenantChange);
        return () => window.removeEventListener("TenantIdChange", handleTenantChange);
    }, []);

    const contactUsUrl = process.env.REACT_APP_CONTACT_US_URL;

    const handleContactUsClick = () => window.open(contactUsUrl, "_blank");
    const handleLogoutClick = () => {
        sessionStorage.clear();
        window.location.href = `${process.env.REACT_APP_SIGN_IN_URL}`;
    };

    return (
        <AppBar position="fixed" color="primary" className="primary">
            <Toolbar sx={{ minHeight: '64px !important' }}>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                    <img src={navLogoUrl} alt="Zamp" style={{ height: 22, marginRight: 8, marginTop: 5 }} />
                </Typography>
                <Box sx={{ flexShrink: 0, marginRight: '40px' }}>
                    <CompanyDropdown />
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button color="inherit" onClick={handleContactUsClick}>Contact Us</Button>
                    <Button color="inherit" onClick={handleLogoutClick} disabled={!tenantId}>Logout</Button>
                </Box>
            </Toolbar>
        </AppBar>
    );
}

function TabsSection() {
    const location = useLocation();
    const navigate = useNavigate();
    const [tenantId, setTenantId] = useState(sessionStorage.getItem("tenantId"));
    const [value, setValue] = useState(0);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuNav, setMenuNav] = useState(false);

    useEffect(() => {
        const handleTenantChange = () => setTenantId(sessionStorage.getItem("tenantId"));
        window.addEventListener("TenantIdChange", handleTenantChange);
        return () => window.removeEventListener("TenantIdChange", handleTenantChange);
    }, []);

    // Update selected tab based on pathname
    useEffect(() => {
        if (menuNav) return;
        if (location.pathname.startsWith("/integration")) setValue(0);
        else if (location.pathname.startsWith("/settings")) setValue(1);
        else if (location.pathname.startsWith("/services")) setValue(2);
        else if (location.pathname.startsWith("/taxcodemapping")) setValue(3);
        else if (location.pathname.startsWith("/customerentitymapping")) setValue(4);
        else if (location.pathname.startsWith("/logs")) setValue(5);
    }, [location.pathname, menuNav]);

    const handleChange = (event, newValue) => {
        if (newValue === 2) {
            // For Data Sync tab, always show the menu (don't toggle, just open)
            setMenuAnchor(event.currentTarget);
            return;
        }

        setValue(newValue);
        setMenuAnchor(null); // Close menu when switching tabs

        switch (newValue) {
            case 0: navigate(`/integration/${tenantId || ""}`); break;
            case 1: navigate(`/settings/${tenantId}`); break;
            case 3: navigate(`/taxcodemapping/${tenantId}`); break;
            case 4: navigate(`/customerentitymapping/${tenantId}`); break;
            case 5: navigate(`/logs/${tenantId}`); break;
            default: break;
        }
    };

    const handleDataSyncClick = (event) => {
        // Always open menu when Data Sync tab is clicked
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClick = (item) => {
        setMenuAnchor(null);
        setMenuNav(true);
        setValue(2);

        if (item === "Historical Sync") navigate(`/services/${tenantId}?mode=full`);
        else if (item === "Quotes") navigate(`/quotes/${tenantId}`);
        else if (item === "Credit Notes") navigate(`/creditnotes/${tenantId}?type=creditnotes`);

        setTimeout(() => setMenuNav(false), 10);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
                backgroundColor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
                overflow: 'auto',
                overflowY: 'hidden',
            }}
        >
            <Tabs
                value={value >= 0 ? value : false}
                onChange={handleChange}
                textColor="inherit"
                centered
                TabIndicatorProps={{
                    style: {
                        backgroundColor: '#ff8232',
                        height: 3,
                        bottom: 0,
                        position: 'absolute',
                    }
                }}
                sx={{
                    minHeight: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    '& .MuiTabs-indicator': {
                        bottom: 0,
                    },
                    '& .MuiTabs-flexContainer': {
                        flexWrap: 'nowrap',
                    },
                    '& .MuiTab-root': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontWeight: 500,
                        minHeight: '48px',
                        maxHeight: '48px',
                        fontSize: 'inherit',
                        padding: '6px 16px',
                        margin: '4px',
                        borderRadius: '0px',
                        transition: 'background-color 0.2s ease, border-radius 0.2s ease',
                        textTransform: 'none',
                        letterSpacing: 'inherit',
                        backgroundColor: 'transparent',
                        opacity: 1,
                        '&:hover:not(.Mui-disabled)': {
                            backgroundColor: 'rgba(24, 62, 61, 0.08)',
                            borderRadius: '24px',
                        },
                        '&.Mui-selected': {
                            color: '#183e3d',
                            fontWeight: 700,
                            backgroundColor: 'transparent',
                            opacity: 1,
                        },
                        '&.Mui-disabled': {
                            color: 'rgba(0, 0, 0, 0.26)',
                            opacity: 1,
                        },
                    },
                }}
            >
                <Tab label="Integration" />
                <Tab label="Zamp Tax Settings" disabled={!tenantId} />
                <Tab
                    label="Data Sync"
                    disabled={!tenantId}
                    onClick={handleDataSyncClick}
                />
                <Tab label="Tax Code Mapping" disabled={!tenantId} />
                <Tab label="Customer Entity Mapping" disabled={!tenantId} />
                <Tab label="Logs" disabled={!tenantId} />
            </Tabs>

            {menuAnchor && (
                <PositionedMenu
                    anchorEl={menuAnchor}
                    onClose={() => setMenuAnchor(null)}
                    menuItems={["Historical Sync", "Quotes"]}
                    onItemClick={handleMenuClick}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                />
            )}
        </Box>
    );
}

export default App;
